import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
	LondonPng,
	MacaoPng,
	MonacoPng,
	ParisPng,
	RedChipsPng,
	SofiaPng,
	VegasPng,
	VipPng,
} from '../../../assets';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { myTablesRequest } from '../../../store/lobby/lobbyActions';
import {
	selectMyRecentTables,
	selectMyTablesItems,
	selectMyTablesLoading,
} from '../../../store/lobby/lobbySagas';
import { MyTable, RecentTable } from '../../../store/types';
import { localizeTableName } from '../../../utils/helpers/localizeTableName';
import { RootStackParamList } from '../../navigator';

/**
 * The lobby's city art, keyed by the first word of the raw (English) table name the backend
 * sends, "London 60". Tournament tables ("Bronze 3") have no city, so they fall back to the chip.
 */
const ROOM_IMAGES: Record<string, any> = {
	london: LondonPng,
	sofia: SofiaPng,
	paris: ParisPng,
	monaco: MonacoPng,
	macao: MacaoPng,
	vegas: VegasPng,
	vip: VipPng,
};

export const tableImage = (rawName: string) =>
	ROOM_IMAGES[String(rawName).trim().split(/\s+/)[0]?.toLowerCase()] ?? RedChipsPng;

export const formatTableChips = (chips: number) =>
	Math.round(Number(chips) || 0).toLocaleString('en-US');

/** The My Active & Recent Tables mock's palette, shared by the bars and the My Tables popup. */
export const myTablesColors = {
	panel: '#2A0705',
	well: '#160302',
	seat: '#2E0B07',
	row: '#2C0A07',
	rowBorder: '#46170F',
	rim: '#C8962E',
	rimDim: '#7A5A22',
	gold: '#FFD766',
	onGold: '#2A0A04',
	secondary: '#CFC2BD',
	muted: '#9A8A84',
	green: '#2FD866',
} as const;

/** The mock's filled gold buttons (View Tables, Return, Go to Lobby). */
export const MY_TABLES_GOLD_FILL: Array<[string, string]> = [
	['0', '#FFE680'],
	['0.55', '#FFCB45'],
	['1', '#F2A01C'],
];

type Nav = NativeStackNavigationProp<RootStackParamList>;

/** Opens a table the way the lobby does: the game screen seats or reconnects the player. */
export const useOpenTable = () => {
	const { navigate } = useNavigation<Nav>();

	return useCallback(
		(table: MyTable | RecentTable) =>
			navigate('Game', {
				tableId: table.id,
				legacyRoomId: table.legacyRoomId,
				isTournamentRoom: 'isTournamentRoom' in table && !!table.isTournamentRoom,
			}),
		[navigate],
	);
};

/**
 * The player's active and recent tables for the My Tables bar and popup. Fetches on mount for a
 * logged-in player; a guest has none (the endpoint answers 401).
 */
export const useMyTables = () => {
	const dispatch = useDispatch();
	const isLogged = useSelector(selectIsLogged);
	const active: MyTable[] = useSelector(selectMyTablesItems);
	const recent: RecentTable[] = useSelector(selectMyRecentTables);
	const loading: boolean = useSelector(selectMyTablesLoading);

	useEffect(() => {
		if (isLogged) dispatch(myTablesRequest());
	}, [dispatch, isLogged]);

	return {
		active: isLogged ? active : [],
		recent: isLogged ? recent : [],
		loading: isLogged && loading,
	};
};

export const BAR_MAX_SEATS = 3;

export type MyTablesBarSummary = {
	kind: 'active' | 'recent' | 'empty';
	/** Raw table names of the first few tables, for their city art. */
	seats: Array<{ key: string; rawName: string }>;
	/** How many more tables of the same kind there are than seats shown. */
	overflow: number;
	/** The status line: the active count, the last played table, or the empty-state title. */
	message: string;
	/** The empty state's second line; empty otherwise. */
	detail: string;
};

/**
 * What the My Active & Recent Tables bar shows: the active tables when there are any, else the
 * recent ones, else a nudge back into the lobby.
 */
export const useMyTablesBarSummary = (): MyTablesBarSummary => {
	const { t } = useTranslation();
	const { active, recent } = useMyTables();

	if (active.length > 0) {
		return {
			kind: 'active',
			seats: active
				.slice(0, BAR_MAX_SEATS)
				.map(table => ({ key: String(table.id), rawName: table.name })),
			overflow: Math.max(0, active.length - BAR_MAX_SEATS),
			message:
				active.length === 1
					? t('T_SITE_MY_TABLES_BAR_ACTIVE_ONE', { defaultValue: 'You have 1 active table' })
					: t('T_SITE_MY_TABLES_BAR_ACTIVE_MANY', {
							defaultValue: 'You have {{count}} active tables',
							count: active.length,
						}),
			detail: '',
		};
	}

	if (recent.length > 0) {
		return {
			kind: 'recent',
			seats: recent.slice(0, BAR_MAX_SEATS).map(table => ({ key: table.id, rawName: table.name })),
			overflow: Math.max(0, recent.length - BAR_MAX_SEATS),
			message: t('T_SITE_MY_TABLES_BAR_LAST_PLAYED', {
				defaultValue: 'Last played: {{name}}',
				name: localizeTableName(recent[0].name),
			}),
			detail: '',
		};
	}

	return {
		kind: 'empty',
		seats: [],
		overflow: 0,
		message: t('T_SITE_MY_TABLES_BAR_EMPTY_TITLE', { defaultValue: 'No active or recent tables' }),
		detail: t('T_SITE_MY_TABLES_BAR_EMPTY_TEXT', { defaultValue: 'Your next game is waiting!' }),
	};
};
